// Утилиты для работы с конфигами локально (без бэкенда)
// src/utils/configUtils.js

import { getApiUrl } from '../config/api';

// SWR-кэш списка конфигов в localStorage: при следующей загрузке страницы
// список (вместе с флагами стран) отрисовывается МГНОВЕННО из кэша, а сеть
// тихо обновляет его в фоне. Сетевой ответ всегда заменяет кэш, поэтому
// новый/удалённый конфиг появляется/исчезает сразу после ответа сервера.
const CONFIGS_LS_KEY = 'cache:configsList';

/**
 * Синхронно возвращает последний известный список конфигов из localStorage
 * (для мгновенной отрисовки до ответа сети). Пустой массив, если кэша нет.
 */
export const getCachedConfigsListSync = () => {
    try {
        const raw = localStorage.getItem(CONFIGS_LS_KEY);
        const arr = raw ? JSON.parse(raw) : null;
        return Array.isArray(arr) ? arr : [];
    } catch (_) {
        return [];
    }
};

/**
 * Получает список доступных конфигов из папки public/configs/ через API
 */
export const getLocalConfigsList = async () => {
    try {
        // Загружаем список конфигов через API
        try {
            const response = await fetch(getApiUrl('api/files/configs'));
            if (response.ok) {
                const data = await response.json();
                const configs = data.configs || [];
                // Обновляем кэш для мгновенной отрисовки при следующей загрузке
                try { localStorage.setItem(CONFIGS_LS_KEY, JSON.stringify(configs)); } catch (_) { /* quota/приватный режим */ }
                return configs;
            }
        } catch (e) {
            console.log('API недоступен, отдаём кэш из localStorage...');
        }

        // Сеть недоступна — отдаём последний известный список из кэша
        return getCachedConfigsListSync();
    } catch (error) {
        console.error('Ошибка получения списка конфигов:', error);
        return getCachedConfigsListSync();
    }
};

/**
 * Загружает конфиг из public/configs/ напрямую
 */
export const loadLocalConfig = async (filename) => {
    try {
        console.log('[loadLocalConfig] Загружаем конфиг:', filename);
        
        // Пробуем загрузить через API endpoint
        try {
            const apiUrl = getApiUrl(`api/files/configs/${encodeURIComponent(filename)}`);
            console.log('[loadLocalConfig] API URL:', apiUrl);
            
            const response = await fetch(apiUrl);
            console.log('[loadLocalConfig] Ответ сервера:', response.status, response.statusText);
            
            if (response.ok) {
                const data = await response.json();
                console.log('[loadLocalConfig] Конфиг успешно загружен');
                return data;
            }
            
            if (response.status === 404) {
                throw new Error(`Конфиг ${filename} не найден`);
            }
            
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        } catch (apiError) {
            console.error('[loadLocalConfig] Ошибка API:', apiError);
            
            // Если API не работает, пробуем напрямую из /configs/
            console.log('[loadLocalConfig] Пробуем прямой путь /configs/...');
            try {
                const directUrl = `/configs/${encodeURIComponent(filename)}`;
                console.log('[loadLocalConfig] Прямой URL:', directUrl);
                const response = await fetch(directUrl);
                
                if (response.ok) {
                    const data = await response.json();
                    console.log('[loadLocalConfig] Конфиг загружен через прямой путь');
                    return data;
                }
            } catch (directError) {
                console.error('[loadLocalConfig] Ошибка прямого пути:', directError);
            }
            
            throw apiError;
        }
    } catch (error) {
        console.error('[loadLocalConfig] Финальная ошибка:', error);
        throw error;
    }
};

/**
 * Сохраняет конфиг напрямую на сервер через API
 */
export const saveLocalConfig = async (filename, configData) => {
    try {
        const apiUrl = getApiUrl(`api/files/configs/${filename}`);
        console.log('Сохраняем конфиг:', filename, 'на сервер через', apiUrl);
        // Токен нужен, чтобы сервер записал в аудит, КТО сохранил конфиг
        let authToken = null;
        try { authToken = localStorage.getItem('authToken'); } catch (_) { /* ssr/недоступно */ }
        // Сохраняем напрямую на сервер через API
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
            body: JSON.stringify({ data: configData })
        });
        
        console.log('Ответ сервера:', response.status, response.statusText);
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Ошибка HTTP:', response.status, errorText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Результат сохранения:', result);
        
        if (!result.success) {
            throw new Error(result?.error || 'Ошибка сохранения файла');
        }
        
        return { success: true, message: 'Конфиг сохранен на сервер' };
    } catch (error) {
        console.error('Ошибка сохранения конфига:', error);
        // НЕ скачиваем файл автоматически - показываем ошибку
        throw new Error(`Не удалось сохранить на сервер: ${error.message}. Убедитесь, что сервер запущен (npm run dev)`);
    }
};

/**
 * Скачивает конфиг как файл
 */
export const downloadConfigFile = (filename, configData) => {
    const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Загружает конфиг из файла (через input file) БЕЗ сохранения на сервер
 * Сохранение должно быть вызвано отдельно через saveLocalConfig
 */
export const importConfigFromFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const configData = JSON.parse(e.target.result);
                const filename = file.name.endsWith('.json') ? file.name : `${file.name}.json`;
                
                // НЕ сохраняем автоматически - возвращаем данные для сохранения вручную
                resolve({ filename, data: configData });
            } catch (error) {
                reject(new Error('Неверный формат JSON файла'));
            }
        };
        reader.onerror = () => reject(new Error('Ошибка чтения файла'));
        reader.readAsText(file);
    });
};

/**
 * Удаляет конфиг из localStorage
 */
export const deleteLocalConfig = (filename) => {
    localStorage.removeItem(`config_${filename}`);
    
    // Обновляем список
    getLocalConfigsList().then(configsList => {
        const updated = configsList.filter(c => c.filename !== filename);
        localStorage.setItem('localConfigsList', JSON.stringify(updated));
    });
};

